import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronRight, Search } from 'lucide-react'

interface ConnectorDef {
  type: string
  name: string
  category: string
  bg: string
  letter: string
  available: boolean
}

const CATALOG: ConnectorDef[] = [
  // Relational Databases
  { type: 'mysql',      name: 'MySQL',          category: 'Relational Databases',     bg: '#00758f', letter: 'M',  available: true },
  { type: 'postgresql', name: 'PostgreSQL',     category: 'Relational Databases',     bg: '#336791', letter: 'Pg', available: true },
  { type: 'mssql',      name: 'SQL Server',     category: 'Relational Databases',     bg: '#c8102e', letter: 'S',  available: false },
  { type: 'oracle',     name: 'Oracle',         category: 'Relational Databases',     bg: '#f80000', letter: 'O',  available: false },
  { type: 'mariadb',    name: 'MariaDB',        category: 'Relational Databases',     bg: '#003545', letter: 'Mb', available: false },
  { type: 'sqlite',     name: 'SQLite',         category: 'Relational Databases',     bg: '#0f80cc', letter: 'Sq', available: false },
  // Data Warehouses
  { type: 'snowflake',  name: 'Snowflake',      category: 'Cloud & Data Warehouses',  bg: '#29b5e8', letter: 'Sf', available: false },
  { type: 'bigquery',   name: 'BigQuery',       category: 'Cloud & Data Warehouses',  bg: '#4285f4', letter: 'Bq', available: false },
  { type: 'redshift',   name: 'Redshift',       category: 'Cloud & Data Warehouses',  bg: '#8c4fff', letter: 'Rs', available: false },
  { type: 'databricks', name: 'Databricks',     category: 'Cloud & Data Warehouses',  bg: '#ff3621', letter: 'D',  available: false },
  { type: 'clickhouse', name: 'ClickHouse',     category: 'Cloud & Data Warehouses',  bg: '#ffcc00', letter: 'Ch', available: false },
  // Files & Storage
  { type: 'sftp',       name: 'SFTP',           category: 'Files & Storage',          bg: '#546e7a', letter: 'Sf', available: true },
  { type: 's3',         name: 'Amazon S3',      category: 'Files & Storage',          bg: '#ff9900', letter: 'S3', available: false },
  { type: 'gcs',        name: 'Google Cloud Storage', category: 'Files & Storage',   bg: '#4285f4', letter: 'Gc', available: false },
  { type: 'azure-blob', name: 'Azure Blob',     category: 'Files & Storage',          bg: '#0078d4', letter: 'Az', available: false },
  // SaaS Applications
  { type: 'salesforce', name: 'Salesforce',     category: 'SaaS Applications',        bg: '#00a1e0', letter: 'Sf', available: false },
  { type: 'hubspot',    name: 'HubSpot',        category: 'SaaS Applications',        bg: '#ff7a59', letter: 'H',  available: false },
  { type: 'stripe',     name: 'Stripe',         category: 'SaaS Applications',        bg: '#635bff', letter: 'St', available: false },
  { type: 'shopify',    name: 'Shopify',        category: 'SaaS Applications',        bg: '#96bf48', letter: 'Sh', available: false },
]

const CATEGORIES = Array.from(new Set(CATALOG.map(c => c.category)))

function ConnectorIcon({ def, size = 40 }: { def: ConnectorDef; size?: number }) {
  return (
    <div
      className="rounded-lg flex items-center justify-center shrink-0 text-white font-bold select-none"
      style={{
        width: size, height: size, fontSize: size * 0.28,
        backgroundColor: def.available ? def.bg : '#d0d5dd',
        letterSpacing: '-0.02em',
      }}
    >
      {def.letter}
    </div>
  )
}

export function BrainerFunnelCatalog() {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')

  const q = search.toLowerCase()
  const filtered = CATALOG.filter(c =>
    c.name.toLowerCase().includes(q) || c.category.toLowerCase().includes(q)
  )
  const visibleCategories = CATEGORIES.filter(cat => filtered.some(c => c.category === cat))

  return (
    <div className="max-w-4xl">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 mb-5">
        <button onClick={() => navigate('/brainer/funnels')} className="text-sm text-ink-dim hover:text-ink">
          Connectors
        </button>
        <ChevronRight size={13} className="text-ink-dim" />
        <span className="text-sm font-medium text-ink">Add connector</span>
      </div>

      <h1 className="text-xl font-semibold text-ink mb-1">Add a connector</h1>
      <p className="text-sm text-ink-dim mb-5">Choose your data source to connect to Brainer Funnel</p>

      {/* Search */}
      <div className="relative mb-6 max-w-sm">
        <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-dim pointer-events-none" />
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search connectors..."
          className="field pl-8 text-sm"
        />
      </div>

      {/* Catalog grouped by category */}
      <div className="space-y-8">
        {visibleCategories.map(category => {
          const items = filtered.filter(c => c.category === category)
          return (
            <div key={category}>
              <h2 className="text-theme-xs font-semibold uppercase tracking-widest text-ink-dim mb-3">
                {category}
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                {items.map(def => (
                  <button
                    key={def.type}
                    disabled={!def.available}
                    onClick={() => navigate(`/brainer/funnels/new?type=${def.type}`)}
                    className={`relative flex items-center gap-3 px-3 py-3 rounded-lg border text-left transition-all ${
                      def.available
                        ? 'border-paper-border bg-white hover:border-accent/40 hover:shadow-sm cursor-pointer'
                        : 'border-paper-border bg-paper-sunken cursor-not-allowed opacity-70'
                    }`}
                  >
                    <ConnectorIcon def={def} size={36} />
                    <div className="min-w-0">
                      <p className={`text-sm font-medium truncate ${def.available ? 'text-ink' : 'text-ink-dim'}`}>
                        {def.name}
                      </p>
                      {!def.available && (
                        <span className="text-[10px] text-ink-dim font-medium">Coming soon</span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )
        })}

        {visibleCategories.length === 0 && (
          <p className="text-sm text-ink-dim py-8 text-center">No connectors match "{search}"</p>
        )}
      </div>
    </div>
  )
}
